/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/mohSourcesApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    API functions for MOH Sources.
    Centralizes all API calls related to MOH sources.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  MohSourcesFetchParams,
  ListMohSourcesResponse,
  MohSourceResponse,
  MOHSource,
  MOHSourceFilters,
} from '../types';

/**
 * Fetch MOH Sources with pagination and filters
 */
export async function fetchMohSources(
  params: MohSourcesFetchParams,
  filters?: MOHSourceFilters,
  domainId?: number,
): Promise<ListMohSourcesResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search && { search: params.search }),
    ...(filters?.class_name && { class_name: filters.class_name }),
    ...(filters?.source_type && { source_type: filters.source_type }),
    ...(filters?.enabled !== undefined && { enabled: filters.enabled.toString() }),
    ...(domainId != null && { domainId: domainId.toString() }),
  });

  const url = `${buildApiUrl('/api/telephony/moh-sources')}?${queryParams}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const payload = await response.json();

  if (!payload?.success) {
    throw new Error(payload?.message || 'Failed to fetch MOH sources');
  }

  return {
    success: true,
    data: payload.data || [],
    pagination: payload.pagination || {
      page: params.page,
      limit: params.limit,
      total: 0,
      totalPages: 0,
    },
  };
}

/**
 * Fetch single MOH Source by ID
 */
export async function fetchMohSourceById(id: string | number): Promise<MohSourceResponse> {
  const response = await fetch(buildApiUrl(`/api/telephony/moh-sources/${id}`), {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch MOH source');
  }

  return result;
}

/**
 * Create MOH Source
 */
export async function createMohSource(formData: FormData): Promise<MohSourceResponse> {
  const headers = getApiHeaders();
  delete (headers as any)['Content-Type']; // Let browser set multipart boundary

  const response = await fetch(buildApiUrl('/api/telephony/moh-sources'), {
    method: 'POST',
    headers,
    body: formData,
  });

  const responseText = await response.text();
  let result;
  try {
    result = JSON.parse(responseText);
  } catch (e) {
    throw new Error('Invalid response from server');
  }

  if (!response.ok) {
    const errorMsg = result?.error || result?.message || 'Failed to create MOH source';
    throw new Error(errorMsg);
  }

  return result;
}

/**
 * Update MOH Source
 */
export async function updateMohSource(id: string | number, formData: FormData): Promise<MohSourceResponse> {
  const headers = getApiHeaders();
  delete (headers as any)['Content-Type']; // Let browser set multipart boundary

  const response = await fetch(buildApiUrl(`/api/telephony/moh-sources/${id}`), {
    method: 'PUT',
    headers,
    body: formData,
  });

  const responseText = await response.text();
  let result;
  try {
    result = JSON.parse(responseText);
  } catch (e) {
    throw new Error('Invalid response from server');
  }

  if (!response.ok) {
    const errorMsg = result?.error || result?.message || 'Failed to update MOH source';
    throw new Error(errorMsg);
  }

  return result;
}

/**
 * Delete MOH Source
 */
export async function deleteMohSource(id: string | number): Promise<void> {
  const response = await fetch(buildApiUrl(`/api/telephony/moh-sources/${id}`), {
    method: 'DELETE',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to delete MOH source');
  }
}

/**
 * Duplicate MOH Source
 */
export async function duplicateMohSource(moh: MOHSource): Promise<MohSourceResponse> {
  const duplicate = { ...moh };
  delete duplicate.id;
  duplicate.name = `${duplicate.name} (Copy)`;

  const response = await fetch(buildApiUrl('/api/telephony/moh-sources'), {
    method: 'POST',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(duplicate),
  });

  if (!response.ok) {
    throw new Error('Failed to duplicate MOH source');
  }

  const result = await response.json();
  return result;
}

/**
 * Get MOH Source XML
 */
export async function getMohSourceXml(id: string | number): Promise<string> {
  const response = await fetch(buildApiUrl(`/api/telephony/moh-sources/${id}/xml`), {
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return await response.text();
}

